import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';

import { AuthProvider } from './contexts/AuthContext';

import Layout from './components/layout/Layout';

import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import LoginPage from './pages/LoginPage';
import ExperiencePage from './pages/ExperiencePage';
import SkillsPage from './pages/SkillsPage';
import PortfolioPage from './pages/PortfolioPage';
import ContactPage from './pages/ContactPage';

const theme = {
  colors: {
    primary: '#E41B1B',
    secondary: '#FFFFFF',
    dark: '#170303',
    light: '#2A0A0A',
    text: '#FFFFFF',
    textLight: '#FFFFFF',
    background: '#120202',
    error: '#EE6D6D'
  },
  fonts: {
    main: "'Poppins', sans-serif"
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px'
  }
};

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
  
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: ${({ theme }) => theme.fonts.main};
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  a {
    text-decoration: none;
    color: inherit;
  }
  
  button {
    cursor: pointer;
    font-family: inherit;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
`;

const App: React.FC = () => {
  const location = useLocation();
  
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AuthProvider>
        <AppContainer>
          <AnimatePresence mode="wait">
            <Layout>
              <Routes location={location} key={location.pathname}>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<Navigate to="/login" replace />} />
                  <Route path="/recovery" element={<Navigate to="/login" replace />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/experience" element={<ExperiencePage />} />
                  <Route path="/skills" element={<SkillsPage />} />
                  <Route path="/portfolio" element={<PortfolioPage />} />
                  <Route path="/contact" element={<ContactPage />} />
              </Routes>
            </Layout>
          </AnimatePresence>
        </AppContainer>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
